<template>
  <div
    ref="triggerRef"
    class="relative"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @click="onTriggerClick"
  >
    <slot />
    <teleport to="body">
      <div
        v-if="isOpen"
        ref="submenuRef"
        class="fixed z-9999 rounded-12 bg-elev-context-menu-bg p-1 shadow-overlay"
        :class="{ invisible: !isPositioned }"
        :style="submenuStyle"
        data-test="hover-submenu-panel"
        @mouseenter="onSubmenuMouseEnter"
        @mouseleave="onSubmenuMouseLeave"
        @pointerdown.stop
      >
        <slot name="submenu" />
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, type CSSProperties } from 'vue'

import { isMobileLayout } from '@/shared/composables/use-mobile'

import { getHoverSubmenuPosition } from '../lib/get-hover-submenu-position'

const CLOSE_DELAY_MS = 120

const triggerRef = ref<HTMLElement | null>(null)
const submenuRef = ref<HTMLElement | null>(null)
const isOpen = ref(false)
const isPositioned = ref(false)
const submenuStyle = ref<CSSProperties>({})

let closeTimer: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | null = null

const getTriggerRect = () => {
  const trigger = triggerRef.value
  if (!trigger) {
    return null
  }

  const item = trigger.querySelector('.wui-list-item')
  return (item instanceof HTMLElement ? item : trigger).getBoundingClientRect()
}

const getSubmenuAlignOffsetY = (submenu: HTMLElement) => {
  const item = submenu.querySelector('.wui-list-item') ?? submenu.firstElementChild
  if (item instanceof HTMLElement) {
    return item.getBoundingClientRect().top - submenu.getBoundingClientRect().top
  }

  return Number.parseFloat(getComputedStyle(submenu).paddingTop) || 0
}

const updatePosition = () => {
  const triggerRect = getTriggerRect()
  const submenu = submenuRef.value
  if (!triggerRect) {
    return
  }

  const position = getHoverSubmenuPosition(
    triggerRect,
    {
      width: submenu?.offsetWidth ?? 0,
      height: submenu?.offsetHeight ?? 0,
    },
    {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    {
      offsetY: submenu ? getSubmenuAlignOffsetY(submenu) : 0,
    },
  )

  submenuStyle.value = {
    top: `${position.top}px`,
    left: `${position.left}px`,
  }
}

const observeSubmenu = () => {
  resizeObserver?.disconnect()
  if (!submenuRef.value || typeof ResizeObserver === 'undefined') {
    return
  }

  resizeObserver = new ResizeObserver(() => {
    updatePosition()
  })
  resizeObserver.observe(submenuRef.value)
}

const open = async () => {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = undefined
  }

  isOpen.value = true
  isPositioned.value = false
  await nextTick()
  updatePosition()
  await nextTick()
  updatePosition()
  isPositioned.value = true
  observeSubmenu()
}

const close = () => {
  isOpen.value = false
  isPositioned.value = false
  resizeObserver?.disconnect()
  resizeObserver = null
}

const scheduleClose = () => {
  if (isMobileLayout.value) {
    return
  }

  if (closeTimer) {
    clearTimeout(closeTimer)
  }
  closeTimer = setTimeout(() => {
    close()
    closeTimer = undefined
  }, CLOSE_DELAY_MS)
}

const onMouseEnter = () => {
  if (isMobileLayout.value) {
    return
  }
  void open()
}

const onMouseLeave = () => {
  if (isMobileLayout.value) {
    return
  }
  scheduleClose()
}

const onSubmenuMouseEnter = () => {
  if (isMobileLayout.value) {
    return
  }
  void open()
}

const onSubmenuMouseLeave = () => {
  if (isMobileLayout.value) {
    return
  }
  scheduleClose()
}

const onTriggerClick = (event: MouseEvent) => {
  if (!isMobileLayout.value) {
    return
  }

  event.stopPropagation()
  if (isOpen.value) {
    close()
    return
  }
  void open()
}

const onViewportChange = () => {
  if (isOpen.value) {
    updatePosition()
  }
}

const onDocumentPointerDown = (event: PointerEvent) => {
  if (!isOpen.value) {
    return
  }

  const target = event.target
  if (!(target instanceof Node)) {
    return
  }

  if (triggerRef.value?.contains(target) || submenuRef.value?.contains(target)) {
    return
  }

  close()
}

onMounted(() => {
  window.addEventListener('resize', onViewportChange)
  window.addEventListener('scroll', onViewportChange, true)
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
})

onBeforeUnmount(() => {
  if (closeTimer) {
    clearTimeout(closeTimer)
  }
  resizeObserver?.disconnect()
  window.removeEventListener('resize', onViewportChange)
  window.removeEventListener('scroll', onViewportChange, true)
  document.removeEventListener('pointerdown', onDocumentPointerDown, true)
})

const getSubmenuElement = () => submenuRef.value

defineExpose({
  close,
  getSubmenuElement,
})
</script>
